import type { StoreOverview } from "@/features/store-overview/storeOverviewSchemas";
import { formatComparisonDelta, formatStoreOverviewAmount, formatStoreOverviewKpi } from "@/features/store-overview/storeOverviewPresentation";

export interface OverviewPanelData {
  readonly headline: { title: string; summary: string; asOf: string | null; gmv: string; gmvDelta: string | null };
  readonly coverage: { covered: number; expected: number; snapshot: string };
  readonly alertCounts: readonly { label: string; value: number | null }[];
  readonly kpis: readonly { key: string; label: string; value: string; delta: string | null; comparisonHint: string }[];
  readonly stores: readonly { id: string; name: string; provider: string; market: string; value: string; share: number | null; profit: number | null; growth: number | null; efficiency: number | null }[];
  readonly attention: readonly { id: string; title: string; provider: string; severity: "critical" | "warning" | "info" }[];
  readonly series: StoreOverview["series"];
  readonly platform: readonly { name: string; share: number | null; value: string }[];
  readonly market: readonly { name: string; share: number | null; value: string }[];
}

export function toOverviewPanelData(overview: StoreOverview): OverviewPanelData {
  return {
    headline: {
      title: overview.headline.title,
      summary: overview.headline.summary,
      asOf: overview.asOf,
      gmv: formatStoreOverviewKpi(overview.kpis.find((item) => item.key === "gross_sales_minor") ?? overview.kpis[0]!),
      gmvDelta: formatComparisonDelta(overview.kpis.find((item) => item.key === "gross_sales_minor")?.comparisonDeltaBps ?? null),
    },
    coverage: {
      covered: overview.coverage.sources.covered,
      expected: overview.coverage.sources.expected,
      snapshot: overview.snapshotId.slice(0, 12),
    },
    alertCounts: [
      { label: "广告异常", value: null },
      { label: "库存风险", value: overview.alerts.filter((item) => item.kind === "low_stock").length },
      { label: "授权异常", value: null },
    ],
    kpis: overview.kpis.map((item) => ({
      key: item.key,
      label: item.label,
      value: formatStoreOverviewKpi(item),
      delta: formatComparisonDelta(item.comparisonDeltaBps),
      comparisonHint: item.comparisonValue === null ? "暂无同周期对比" : "较上一周期",
    })),
    stores: overview.rankings.stores.map((item) => ({
      id: item.resourceId,
      name: item.label,
      provider: item.provider,
      market: item.market,
      value: formatStoreOverviewAmount(item.value, item.currency),
      share: item.shareBps === null ? null : Number(item.shareBps) / 100,
      profit: item.mockMetrics ? Number(item.mockMetrics.profit_minor) / 100 : null,
      growth: item.mockMetrics ? Number(item.mockMetrics.growth_bps) / 100 : null,
      efficiency: item.mockMetrics ? Number(item.mockMetrics.ad_efficiency_bps) / 10000 : null,
    })),
    attention: overview.attention.map((item) => ({
      id: item.id,
      title: item.title,
      provider: item.provider || "经营数据",
      severity: item.severity,
    })),
    series: overview.series,
    platform: overview.composition.platform.map((item) => ({
      name: item.label,
      share: item.shareBps === null ? null : Number(item.shareBps) / 100,
      value: formatStoreOverviewAmount(item.value, item.currency),
    })),
    market: overview.composition.market.map((item) => ({
      name: item.label,
      share: item.shareBps === null ? null : Number(item.shareBps) / 100,
      value: formatStoreOverviewAmount(item.value, item.currency),
    })),
  };
}

